import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Markdown from "react-markdown";
import { trim } from "lodash";

import { useQuery } from "../../hooks";
import LoadingProgress from "./LoadingProgress";

import styles from "./Results.module.css";
import { Stack } from "react-bootstrap";

const TEN_SECONDS = 10000;
const FORTY_SECONDS = 40000;
const ONE_MINUTE = 60 * 1000;
const THREE_MINUTES = 3 * ONE_MINUTE;
const THIRTY_MINUTES = 30 * ONE_MINUTE;

function queuedMoreThan30MinsAgo(data: { queued_at?: string }) {
  if (data?.queued_at) {
    const queuedAt = new Date(data.queued_at).getTime();
    const now = Date.now();
    return now - queuedAt > THIRTY_MINUTES;
  }
  return false;
}

export default function Results() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [refetches, setRefetches] = useState({ queued: 0, processing: 0 });
  const { data, isFetching, refetch } = useQuery("prompt_results", id, {
    enabled: !!id,
  });

  useEffect(() => {
    if (!isFetching && !data?.displayed_response) {
      // Return early if data.queued_at is more than 30 minutes ago
      if (queuedMoreThan30MinsAgo(data)) return;

      if (!loading) setLoading(true);
      const delay = getDelay(data, refetches);
      if (data?.processing_started) {
        setRefetches(prev => ({ ...prev, processing: prev.processing + 1 }));
      } else {
        setRefetches(prev => ({ ...prev, queued: prev.queued + 1 }));
      }
      const timeout = setTimeout(() => {
        refetch();
      }, delay);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, data]);

  // this is to prevent refreshing the page too often
  function getDelay(data: { error?: string }, refetches: { queued: number, processing: number }) {
    if (data?.error) {
      return THREE_MINUTES;
    }
    if (refetches.queued === 0) {
      return TEN_SECONDS;
    }
    if (refetches.processing === 0) {
      return FORTY_SECONDS;
    }
    return TEN_SECONDS;
  }

  function renderContent() {
    if (data?.error) return <Markdown>{data.error}</Markdown>;
    
    if (loading) {
      if (data?.queued && !data?.processing_started) {
        return <p>
          Your Values and Strengths have been submitted!
          Processing will start once earlier requests are complete.
        </p>;
      }
      
      const noResult = !(data?.error || data?.displayed_response);
      return <LoadingProgress noResult={noResult} setLoading={setLoading} />;
    }

    if (data?.displayed_response)
      return (
        <Stack gap={5}>
          <Stack gap={2}>
            <p>Congratulations on completing the Integrated Life Purpose Assessment!</p>
            <p>You've taken a meaningful step in exploring how your God-given values and strengths can shape a life of purpose and generosity. These statements were designed to offer insight and inspiration for your journey.</p>
            <p>Each expanded statement gives you a different way to see what living with purpose might look like. Take time with them. Notice which parts feel true for you and give yourself space to reflect on others more deeply.</p>
            <p>Exploring your purpose is an ongoing journey. As you continue to grow and gain new perspective, let these statements serve as gentle anchors, helping you live and give in ways that feel authentic, intentional, and aligned with who you're created to be.</p>
          </Stack>
          <div>
            <h3>Values and Strengths Selected:</h3>
            <p>
              <strong>Values:</strong> {data.values?.replaceAll("\n", ", ")}
            </p>
            <p>
              <strong>Strengths:</strong>{" "}
              {data.strengths.replaceAll("\n", ", ")}
            </p>
          </div>
          <div>
            <Markdown>{trim(data.pair_explanation)}</Markdown>
          </div>
          <div>
            <h2>Expanded Life Purpose Statements</h2>
            <Markdown>{trim(data.displayed_response)}</Markdown>
          </div>
        </Stack>
      );
  }

  return (
    <div className={styles.results}>
      {renderContent()}
      <br />
      <Link className="button" to="/">
        Home
      </Link>
    </div>
  );
}
